import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getProducts } from '@/api/services/product'
import type { GetProductsQuery, GetProductsResponse, Product } from '@/api/types/product'

type UseInfiniteProductsOptions = {
  /** ベースとなるクエリ（domain, tags, order_by, etc.） */
  baseQuery: GetProductsQuery
  domain: string | undefined
  perPage?: number
}

type UseInfiniteProductsReturn = {
  /** ロード済みページMap（ページ番号 → Product[]） */
  loadedPages: Map<number, Product[]>
  /** フラット化された全プロダクト配列（ソート済み） */
  allProducts: Product[]
  /** 最小ロード済みページ番号 */
  minPage: number
  /** 最大ロード済みページ番号 */
  maxPage: number
  /** 総ページ数（API由来） */
  totalPages: number
  /** 指定ページをフェッチ */
  fetchPage: (pageNum: number) => Promise<void>
  /** 上方向（前ページ）のロード中フラグ */
  isLoadingPrev: boolean
  /** 下方向（次ページ）のロード中フラグ */
  isLoadingNext: boolean
  /** 初期ロード済みフラグ */
  isInitialized: boolean
  /** キャッシュクリア */
  reset: () => void
  /** ロード済み全ページを再取得 */
  refetchAll: () => Promise<void>
  /** ローカルデータ内の特定エピソードの進捗を更新 */
  updateEpisodeProgress: (episodeId: number, progress: number, isCompleted: boolean) => void
  /** ローカルデータ内の特定プロダクトのタイトルを更新 */
  updateProductTitle: (productId: number, title: string) => void
}

export const useInfiniteProducts = ({
  baseQuery,
  domain,
  perPage = 60,
}: UseInfiniteProductsOptions): UseInfiniteProductsReturn => {
  const [loadedPages, setLoadedPages] = useState<Map<number, Product[]>>(new Map())
  const [totalPages, setTotalPages] = useState(1)
  const [isLoadingPrev, setIsLoadingPrev] = useState(false)
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // refで最新値を追跡（fetchPageの依存配列を安定化）
  const loadedPagesRef = useRef(loadedPages)
  const totalPagesRef = useRef(totalPages)
  const isInitializedRef = useRef(isInitialized)
  useEffect(() => {
    loadedPagesRef.current = loadedPages
    totalPagesRef.current = totalPages
    isInitializedRef.current = isInitialized
  }, [loadedPages, totalPages, isInitialized])

  // 重複リクエスト防止
  const fetchingRef = useRef<Set<number>>(new Set())

  const fetchPage = useCallback(
    async (pageNum: number) => {
      // 既にロード済みorロード中ならスキップ
      if (fetchingRef.current.has(pageNum)) return
      if (loadedPagesRef.current.has(pageNum)) return

      // ページ番号バリデーション
      if (pageNum < 1) return

      // 初期化済みで範囲外ならスキップ
      if (isInitializedRef.current && pageNum > totalPagesRef.current) return

      fetchingRef.current.add(pageNum)

      // ロード方向に基づいてフラグを設定
      const currentPages = loadedPagesRef.current
      const minLoaded = currentPages.size > 0 ? Math.min(...currentPages.keys()) : pageNum
      const isPrev = pageNum < minLoaded
      if (isPrev) {
        setIsLoadingPrev(true)
      } else {
        setIsLoadingNext(true)
      }

      try {
        const query: GetProductsQuery = {
          ...baseQuery,
          domain,
          page: pageNum,
          per_page: perPage,
          with_episodes: true,
        }

        const response: GetProductsResponse = await getProducts(query)

        setTotalPages(response.last_page)
        setIsInitialized(true)

        setLoadedPages((prev) => {
          const next = new Map(prev)
          next.set(pageNum, response.products)
          return next
        })
      } catch (error) {
        console.error(`Failed to fetch page ${pageNum}:`, error)
      } finally {
        fetchingRef.current.delete(pageNum)
        if (isPrev) {
          setIsLoadingPrev(false)
        } else {
          setIsLoadingNext(false)
        }
      }
    },
    [baseQuery, domain, perPage],
  )

  const reset = useCallback(() => {
    const emptyMap = new Map<number, Product[]>()
    setLoadedPages(emptyMap)
    setTotalPages(1)
    setIsInitialized(false)
    setIsLoadingPrev(false)
    setIsLoadingNext(false)
    fetchingRef.current.clear()
    // refも即座にクリア（直後のfetchPageでスキップされないように）
    loadedPagesRef.current = emptyMap
    isInitializedRef.current = false
    totalPagesRef.current = 1
  }, [])

  /** ロード済み全ページを再取得（プロダクト更新反映用） */
  const refetchAll = useCallback(async () => {
    const pageNums = [...loadedPagesRef.current.keys()]
    if (pageNums.length === 0) return

    setIsLoadingNext(true)
    try {
      const results = await Promise.all(
        pageNums.map(async (pageNum) => {
          const query: GetProductsQuery = {
            ...baseQuery,
            domain,
            page: pageNum,
            per_page: perPage,
            with_episodes: true,
          }
          const response = await getProducts(query)
          return { pageNum, products: response.products, lastPage: response.last_page }
        }),
      )

      setLoadedPages(() => {
        const next = new Map<number, Product[]>()
        for (const { pageNum, products } of results) {
          next.set(pageNum, products)
        }
        return next
      })
      if (results.length > 0) {
        setTotalPages(results[0].lastPage)
      }
    } catch (error) {
      console.error('Failed to refetch pages:', error)
    } finally {
      setIsLoadingNext(false)
    }
  }, [baseQuery, domain, perPage])

  // ローカルデータ内の特定エピソードの進捗を更新
  const updateEpisodeProgress = useCallback((episodeId: number, progress: number, isCompleted: boolean) => {
    setLoadedPages((prev) => {
      let foundAny = false
      const next = new Map<number, Product[]>()
      for (const [pageNum, products] of prev) {
        let foundInPage = false
        const updated = products.map((product) => {
          const hasEpisode = product.episodes.some((ep) => ep.episode_id === episodeId)
          if (!hasEpisode) return product
          foundInPage = true
          return {
            ...product,
            episodes: product.episodes.map((ep) =>
              ep.episode_id === episodeId ? { ...ep, progress, is_completed: isCompleted } : ep,
            ),
          }
        })
        if (foundInPage) foundAny = true
        next.set(pageNum, foundInPage ? updated : products)
      }
      return foundAny ? next : prev
    })
  }, [])

  // ローカルデータ内の特定プロダクトのタイトルを更新
  const updateProductTitle = useCallback((productId: number, title: string) => {
    setLoadedPages((prev) => {
      let foundAny = false
      const next = new Map<number, Product[]>()
      for (const [pageNum, products] of prev) {
        const foundInPage = products.some((product) => product.product_id === productId)
        if (foundInPage) foundAny = true
        next.set(
          pageNum,
          foundInPage ? products.map((p) => (p.product_id === productId ? { ...p, title } : p)) : products,
        )
      }
      return foundAny ? next : prev
    })
  }, [])

  // ソート済みページ番号からフラット化（メモ化）
  const { allProducts, minPage, maxPage } = useMemo(() => {
    const sortedPageNums = [...loadedPages.keys()].sort((a, b) => a - b)
    const min = sortedPageNums[0] ?? 1
    const max = sortedPageNums.at(-1) ?? 1
    const products: Product[] = []
    for (const pn of sortedPageNums) {
      const p = loadedPages.get(pn)
      if (p) products.push(...p)
    }
    return { allProducts: products, minPage: min, maxPage: max }
  }, [loadedPages])

  return {
    loadedPages,
    allProducts,
    minPage,
    maxPage,
    totalPages,
    fetchPage,
    isLoadingPrev,
    isLoadingNext,
    isInitialized,
    reset,
    refetchAll,
    updateEpisodeProgress,
    updateProductTitle,
  }
}
